import { isAllFilterSelected, stripFilterSentinels } from "./filterRelationshipUtils";

/**
 * Client-side cascade helpers for the indirect & fringe filter-relationship feeds.
 *
 * Indirect feed  GET /reports/indirect/filter_relationships
 *   Row fields (snake_case): dept, sub_dept, acct_id, acct_name, empl_id, empl_name, cost_type
 *   Two row types share one shape:
 *     - labor row:   empl_id + empl_name set; acct_id / acct_name / cost_type = ""
 *     - account row: acct_id + acct_name + cost_type set; empl_id / empl_name = ""
 *
 * Fringe feed  GET /reports/indirect/fringe/filter_relationships
 *   Row fields: acct_id, acct_name, cost_type
 */

/** Filter keys that cascade from the indirect feed */
export const INDIRECT_CASCADE_KEYS = ["department", "subDepartment", "employeeName"];

/** Filter keys that cascade from the fringe feed */
export const FRINGE_CASCADE_KEYS = ["costTypeFringe"];

/* ─── Normalisers ──────────────────────────────────────────────── */

export function normalizeIndirectRelRows(res) {
  const data = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
  return data;
}

export function normalizeFringeRelRows(res) {
  const data = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
  return data;
}

/* ─── Row field accessors ──────────────────────────────────────── */

function getIndirectRowValues(row, key) {
  if (key === "department")    return row.dept      ? [row.dept]      : [];
  if (key === "subDepartment") return row.sub_dept  ? [row.sub_dept]  : [];
  if (key === "employeeName")  return row.empl_name ? [row.empl_name] : [];
  return [];
}

function getFringeRowValues(row, key) {
  if (key === "costTypeFringe") return row.cost_type ? [row.cost_type] : [];
  return [];
}

/* ─── Row predicates ───────────────────────────────────────────── */

function rowMatchesIndirect(row, key, selected) {
  const vals = getIndirectRowValues(row, key);
  if (!vals.length) return false;
  return selected.some((s) => vals.includes(s));
}

function rowMatchesFringe(row, key, selected) {
  const vals = getFringeRowValues(row, key);
  if (!vals.length) return false;
  return selected.some((s) => vals.includes(s));
}

/* ─── Row filtering ────────────────────────────────────────────── */

/**
 * Return rows satisfying every active indirect selection, optionally skipping
 * one key (so we can compute that key's valid options without self-filtering).
 */
export function filterIndirectRows(rows, filters, excludeKey = null) {
  if (!rows?.length) return [];
  return rows.filter((row) =>
    INDIRECT_CASCADE_KEYS.every((key) => {
      if (excludeKey && key === excludeKey) return true;
      const selected = filters?.[key];
      if (!Array.isArray(selected) || selected.length === 0 || isAllFilterSelected(selected)) return true;
      return rowMatchesIndirect(row, key, stripFilterSentinels(selected));
    }),
  );
}

export function filterFringeRows(rows, filters, excludeKey = null) {
  if (!rows?.length) return [];
  return rows.filter((row) =>
    FRINGE_CASCADE_KEYS.every((key) => {
      if (excludeKey && key === excludeKey) return true;
      const selected = filters?.[key];
      if (!Array.isArray(selected) || selected.length === 0 || isAllFilterSelected(selected)) return true;
      return rowMatchesFringe(row, key, stripFilterSentinels(selected));
    }),
  );
}

/* ─── Distinct value helpers ───────────────────────────────────── */

function distinctSorted(values) {
  return [...new Set(values)]
    .filter(Boolean)
    .sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" }),
    );
}

function collectIndirectValues(rows, key) {
  const vals = [];
  rows.forEach((row) => getIndirectRowValues(row, key).forEach((v) => vals.push(v)));
  return distinctSorted(vals);
}

function collectFringeValues(rows, key) {
  const vals = [];
  rows.forEach((row) => getFringeRowValues(row, key).forEach((v) => vals.push(v)));
  return distinctSorted(vals);
}

/* ─── Public: build cascaded options ──────────────────────────── */

/**
 * For each indirect cascade key, filter all rows by every OTHER active
 * selection, then collect distinct valid values for that key.
 * Currently-selected values are always preserved even if they left the feed.
 */
export function buildIndirectCascadedOptions(allRows, filters) {
  if (!allRows?.length) return {};
  const out = {};
  INDIRECT_CASCADE_KEYS.forEach((key) => {
    const filtered  = filterIndirectRows(allRows, filters, key);
    const cascaded  = collectIndirectValues(filtered, key);
    const selected  = filters?.[key];
    if (Array.isArray(selected) && selected.length && !isAllFilterSelected(selected)) {
      out[key] = distinctSorted([...cascaded, ...stripFilterSentinels(selected)]);
    } else {
      out[key] = cascaded;
    }
  });
  return out;
}

export function buildFringeCascadedOptions(allRows, filters) {
  if (!allRows?.length) return {};
  const out = {};
  FRINGE_CASCADE_KEYS.forEach((key) => {
    const filtered = filterFringeRows(allRows, filters, key);
    const cascaded = collectFringeValues(filtered, key);
    const selected = filters?.[key];
    if (Array.isArray(selected) && selected.length && !isAllFilterSelected(selected)) {
      out[key] = distinctSorted([...cascaded, ...stripFilterSentinels(selected)]);
    } else {
      out[key] = cascaded;
    }
  });
  return out;
}

/* ─── Public: prune invalid selections ────────────────────────── */

/** Drop indirect selections that no longer satisfy the cascade predicate */
export function pruneIndirectSelections(filters, allRows) {
  if (!allRows?.length || !filters) return filters;
  const filtered = filterIndirectRows(allRows, filters);
  const next = { ...filters };
  INDIRECT_CASCADE_KEYS.forEach((key) => {
    const sel = next[key];
    if (!Array.isArray(sel) || sel.length === 0 || isAllFilterSelected(sel)) return;
    const validSet = new Set(collectIndirectValues(filtered, key));
    next[key] = sel.filter((v) => validSet.has(v));
  });
  return next;
}

export function pruneFringeSelections(filters, allRows) {
  if (!allRows?.length || !filters) return filters;
  const filtered = filterFringeRows(allRows, filters);
  const next = { ...filters };
  FRINGE_CASCADE_KEYS.forEach((key) => {
    const sel = next[key];
    if (!Array.isArray(sel) || sel.length === 0 || isAllFilterSelected(sel)) return;
    const validSet = new Set(collectFringeValues(filtered, key));
    next[key] = sel.filter((v) => validSet.has(v));
  });
  return next;
}
